import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import { Action } from '../../enums/action';

// Store
import { Dispatch, RootState } from '../../store/store';

// Types
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';

// Constants
import ROUTE from '../../constants/route-constants';

// Components
import Table from '../../components/Table/Table';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import Modal from '../../components/Modal/Modal';
import IconEditOutlined from '../../components/Icons/IconEditOutlined/IconEditOutlined';
import IconBin from '../../components/Icons/IconBin/IconBin';
import { ElementSize } from '../../types/element-size';
import IconInputOne from '../../components/Icons/IconInputOne/IconInputOne';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import { LocationContract } from '../../store/models/locations';
import { useAbilityContext } from '../../components/Can/Can';
import { PAGE_TITLE } from '../../constants/common';
import { PartialActionMap } from '../../types/partial-action-map';
import Tooltip from '../../components/Tooltip';
import { useTranslation } from 'react-i18next';
import { ROLES } from '../../constants/roles';
import { ButtonType } from '../../types/button-type';

type LocationsViewProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

type LocationAction = PartialActionMap<IActionsDropdown>;

const LocationsView: React.FC<LocationsViewProps> = ({
  locations,
  profile,
  getLocations,
  deleteLocation,
  resetState,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLocationsLoaded, setIsLocationsLoaded] = useState<boolean>(false);
  const [locationToDelete, setLocationToDelete] = useState<any | null>(null);
  // Supervisors see every client in the org, not just ones they're assigned
  // to - lets them hide the ones they can't actually open back down to just
  // their own.
  const isSupervisor = profile?.role === ROLES.SUPERVISOR;
  const [hideUnassignedClients, setHideUnassignedClients] = useState<boolean>(false);

  const ability = useAbilityContext();

  const permission: { [key in Action]: boolean } = useMemo(() => {
    const permission: { [key in Action]?: boolean } = {};
    permission[Action.Create] = ability.can(Action.Create, PAGE_TITLE.CREATE_LOCATION);
    permission[Action.Delete] = ability.can(Action.Delete, PAGE_TITLE.EDIT_LOCATION);
    permission[Action.Update] = ability.can(Action.Update, PAGE_TITLE.EDIT_LOCATION);

    return permission as { [key in Action]: boolean };
  }, [ability]);

  const fetchLocations = async () => {
    await getLocations();

    setIsLocationsLoaded(true);
  };

  const getContractsView = (contracts: LocationContract[]) => {
    let openContracts = 0;
    let closedContracts = 0;

    for (const contract of contracts) {
      if (contract.endless) {
        openContracts++
        continue;
      }

      const endAtTime = new Date(contract.end_at).getTime();
      const currentTime = new Date().getTime();
      if (endAtTime > currentTime) {
        openContracts++
        continue;
      }

      closedContracts++
    };

    return (
      <span className='flex flex-row text-xs'>
        <span className='py-1 w-18 flex justify-center items-center rounded-tl-sm rounded-bl-sm bg-opacity-10 text-xs font-medium bg-success text-green-600'>
          {`open (${openContracts})`}
        </span>
        <span className='py-1 w-18 flex justify-center items-center rounded-tr-sm rounded-br-sm bg-opacity-10 text-xs font-medium bg-red text-red'>
          {`closed (${closedContracts})`}
        </span>
      </span>
    );
  }

  const visibleLocations = (locations ?? []).filter(
    (location) => !(isSupervisor && hideUnassignedClients && !location.hasAccess),
  );

  const locationsView = visibleLocations.map((location) => ({
    Name: location.name,
    City: location.city,
    Contracts: getContractsView(location.contracts),
    'Total cycles': location.count.total,
    'Active cycles': location.count.active,
    id: location.id,
    hasAccess: location.hasAccess,
  }));

  const handleClick = () => {
    navigate(ROUTE.CREATE_LOCATION);
  };

  const handleDeleteLocation = async (id: string) => {
    await deleteLocation(id);
    await fetchLocations();
  };

  const actionsButtons: LocationAction = {
    [Action.Read]: {
      icon: () => (
        <Tooltip hint={t('contract.common.viewContractsByLocation')} >
          <IconInputOne size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (id: string) => {
        const path = generatePath(ROUTE.LOCATION_CONTRACTS, { clientId: id });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
      isShow: (item) => item.hasAccess !== false,
    },
    [Action.Update]: {
      icon: () => (
        <Tooltip hint={t('commonText.edit')} >
          <IconEditOutlined
            stroke="inherit"
            iconClassName="text-inherit"
            size={ElementSize.XXS}
          />
        </Tooltip>
      ),
      onClick: (id: string) => {
        const path = generatePath(ROUTE.EDIT_LOCATION, { id });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
      isShow: (item) => item.hasAccess !== false,
    },
    [Action.Delete]: {
      icon: () => (
        <Tooltip hint={t('commonText.delete')} hintClassName='right-[-90%] bottom-7' isCustomSettings>
          <IconBin fill="#000" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: async (...args) => {
        openModalDeletion(args[1]);
      },
      loading: false,
      isShow: (item) => item.hasAccess !== false,
    },
  };


  const getBtnActions = (action: LocationAction): IActionsDropdown[] => {
    if (!permission.update) {
      delete action.update;
    }

    if (!permission.delete) {
      delete action.delete;
    }

    const actions = Object.values(action);

    return actions;
  };

  const acceptDeleteLocation = () => {
    handleDeleteLocation(locationToDelete.id);
    closeModalDeletion();
  }

  const openModalDeletion = (location) => {
    setLocationToDelete(location);
  }

  const closeModalDeletion = () => {
    setLocationToDelete(null)
  }

  useEffect(() => {
    fetchLocations();

    return () => {
      resetState();
    }
  }, []);

  return (
    <>
      <div className="p-locations-view flex h-full w-full flex-col">
        {!isLocationsLoaded && (
          <div className="flex flex-auto items-center justify-center"><SpinnerLoader /></div>
        )}
        {isLocationsLoaded && (
          <>
            <div className="mb-5 flex w-full justify-between items-center">
              <h2 className="text-title-md2 font-semibold text-black dark:text-white">
                Clients
              </h2>
              <div className="flex items-center gap-3">
                {isSupervisor && (
                  <InteractiveButton
                    type={ButtonType.PrimaryInverse}
                    text={hideUnassignedClients ? 'Show clients without access' : 'Hide clients without access'}
                    onClick={() => setHideUnassignedClients((prev) => !prev)}
                  />
                )}
                {permission.create && (<InteractiveButton text="Create" onClick={handleClick} />)}
              </div>
            </div>
            <Table
              values={locationsView}
              defaultKeys={['Name', 'City', 'Contracts', 'Total cycles', 'Active cycles']}
              actionsButtons={getBtnActions(actionsButtons)}
            />
            <Modal
              modalTitle="Deleting a client"
              isVisible={locationToDelete !== null}
              content={`Are you sure you want to delete ${locationToDelete ? locationToDelete['Name'] : ''} client?`}
              acceptBtnText="Delete"
              onClose={closeModalDeletion}
              onAccept={acceptDeleteLocation}
              showAcceptBtn
              showCancelBtn
            />
          </>
        )}
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  locations: state.locations.locationsWithCycleCount,
  profile: state.members.currentUserMember,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getLocations: dispatch.locations.getLocationsWithCycleCount,
  deleteLocation: dispatch.locations.deleteLocation,
  resetState: dispatch.locations.resetList,
});

export default connect(mapState, mapDispatch)(LocationsView);
